import React, { FunctionComponent, memo, useEffect, useState } from "react";
import {
  Text,
  Link,
  withSitecoreContext,
  Image,
} from "@sitecore-jss/sitecore-jss-react";
import {
  StringField,
  ImageField,
  LinkField,
  CheckboxField,
  VideoSitecoreField,
} from "../../types/types";
import { TextColorField } from "./types";
import useMediaQuery from "../../lib/custom-hooks/useMediaQuery";

export interface HeroCarouselItemFields {
  image: ImageField;
  imageMobile: ImageField;
  title: StringField;
  eyebrow: StringField;
  content: StringField;
  link: LinkField;
  textColor: TextColorField;
  video: VideoSitecoreField;
  useVideo: CheckboxField;
}

interface HeroCarouselItemProps {
  slideInfo?: {
    currentSlide: number;
    index: number;
    networkSpeed: number;
  };
  fields?: HeroCarouselItemFields;
  sitecoreContext: {
    pageEditing: boolean;
  };
  params: StringField;
  carousalType: StringField;
}

const HeroCarouselSlide: FunctionComponent<HeroCarouselItemProps> = ({
  slideInfo,
  fields,
  sitecoreContext,
  params,
  carousalType,
}) => {
  const [mediaVisible, setMediaVisible] = useState({
    video: false,
    image: false,
  });

  const textColor = fields?.textColor?.value || "corporateBlack";
  const variation: StringField = params;

  useEffect(() => {
    if (
      fields?.useVideo?.value &&
      slideInfo?.currentSlide === slideInfo?.index &&
      !mediaVisible.video
    ) {
      setMediaVisible((prevState) => ({ ...prevState, video: true }));
    }

    if (slideInfo?.currentSlide === slideInfo?.index && !mediaVisible.image) {
      setMediaVisible((prevState) => ({ ...prevState, image: true }));
    }
  });

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const showEyeBrowText = () => {
    switch (variation?.value) {
      case "WithEyebrowWithoutDescription":
        return true;
      case "WithEyebrow":
        return true;
      case "WithoutEyebrow":
        return false;
    }
  };

  return (
    <div className={"slide__inner"} data-text-color={textColor}>
      {fields?.useVideo?.value &&
      (sitecoreContext.pageEditing || (slideInfo?.networkSpeed ?? 0) >= 15)
        ? mediaVisible.video && (
            <video
              poster={fields?.image?.value?.src}
              preload="auto"
              autoPlay
              muted
              playsInline
              loop
              className={"slide__video"}
            >
              <source src={fields?.video?.value?.href} />
            </video>
          )
        : mediaVisible.image && (
            <picture>
              <source
                media="(min-width:768px)"
                srcSet={fields?.image?.value?.src}
                style={{ zIndex: sitecoreContext.pageEditing ? 0 : -2 }}
                className={"slide__image desktop"}
              />

              <source
                media="(min-width:320px)"
                srcSet={
                  fields?.imageMobile && fields?.imageMobile?.value
                    ? fields.imageMobile?.value?.src
                    : fields?.image?.value?.src
                }
                style={{ zIndex: sitecoreContext.pageEditing ? 0 : -2 }}
                className={"slide__image mobile"}
              />

              <Image
                field={fields?.image}
                src={fields?.image?.value?.src}
                alt={fields?.image?.value?.alt}
                style={{ zIndex: sitecoreContext.pageEditing ? 0 : -2 }}
                className={"slide__image"}
                width={
                  isDesktop
                    ? fields?.image?.value?.width
                    : fields?.imageMobile?.value?.width
                }
                height={
                  isDesktop
                    ? fields?.image?.value?.height
                    : fields?.imageMobile?.value?.height
                }
              />
            </picture>
          )}
      {fields?.title?.value === "Learn More" ? (
        <div
          className={`slide__content slide__content--${variation} ${
            carousalType?.value === "Small Carousel"
              ? "smallCarousalContent"
              : ""
          } slide__contentBB`}
        >
          {sitecoreContext.pageEditing ? (
            <>
              <Text
                encode={false}
                field={fields.title}
                tag={"h2"}
                className={"slide__text slide__textBB slide__text--title"}
              />
              {fields.content && (
                <Text
                  encode={false}
                  field={fields.content}
                  tag={"span"}
                  className={"slide__text slide__textBB slide__text--content"}
                />
              )}
              <div className="cta cta-primary-red">
                <Link field={fields.link} />
              </div>
            </>
          ) : (
            <>
              {fields.eyebrow && showEyeBrowText() && (
                <Text
                  encode={false}
                  field={fields.eyebrow}
                  tag={"span"}
                  className={`slide__text slide__textBB slide__eyebrow slide__eyebrow--${variation}`}
                />
              )}

              {fields?.title?.value === "Learn More" ? (
                <Text
                  encode={false}
                  field={fields?.title}
                  tag={"h2"}
                  className={`slide__text  slide__text--title_${variation} slide__textAA`}
                />
              ) : (
                <Text
                  encode={false}
                  field={fields.title}
                  tag={"h2"}
                  className={`slide__text  slide__text--title_${variation} slide__textBB`}
                />
              )}

              {fields.content && (
                <Text
                  encode={false}
                  field={fields.content}
                  tag={"span"}
                  className={`slide__text slide__textBB slide__text--content_${variation}`}
                />
              )}

              {fields?.link?.value?.href && (
                <div className="stories__link slide__cta">
                  <Link field={fields.link} />
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div
          className={`slide__content slide__content--${variation} ${
            carousalType?.value === "Small Carousel"
              ? "smallCarousalContent"
              : ""
          } slide__contentAA`}
        >
          {sitecoreContext.pageEditing ? (
            <>
              <Text
                encode={false}
                field={fields?.title}
                tag={"h2"}
                className={"slide__text slide__textBB slide__text--title"}
              />

              {fields?.content && (
                <Text
                  encode={false}
                  field={fields?.content}
                  tag={"span"}
                  className={"slide__text slide__textBB slide__text--content"}
                />
              )}

              <div className="cta cta-primary-red">
                <Link field={fields?.link as LinkField} />
              </div>
            </>
          ) : (
            <>
              {fields?.eyebrow && showEyeBrowText() && (
                <Text
                  encode={false}
                  field={fields?.eyebrow}
                  tag={"span"}
                  className={`slide__text slide__textBB slide__eyebrow slide__eyebrow--${variation}`}
                />
              )}

              {fields?.title.value === "Learn More" ? (
                <Text
                  encode={false}
                  field={fields.title}
                  tag={"h2"}
                  className={`slide__text  slide__text--title_${variation} slide__textAA`}
                />
              ) : (
                <Text
                  encode={false}
                  field={fields?.title}
                  tag={"h2"}
                  className={`slide__text  slide__text--title_${variation} slide__textBB`}
                />
              )}

              {fields?.content && (
                <Text
                  encode={false}
                  field={fields.content}
                  tag={"span"}
                  className={`slide__text slide__textBB slide__text--content_${variation}`}
                />
              )}

              {fields?.link?.value?.href && (
                <div className="cta cta-primary-red">
                  <Link field={fields.link} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default withSitecoreContext()(memo(HeroCarouselSlide));
